import React from "react";
import { Card, Corners, Empty, Ring } from "./TvBits";

const HC = { green: "#10B981", amber: "#F59E0B", red: "#EF4444" };
const fmt = (v) => (v == null ? "—" : v >= 10000 ? `$${(v / 1000).toFixed(1)}K` : `$${Math.round(v).toLocaleString()}`);

const Bars = ({ data, col, h = 120, labels }) => {
  const max = Math.max(1, ...data.map((x) => x.v));
  return (
    <div className="relative" style={{ height: h }}>
      {[0.25, 0.5, 0.75].map((g) => <div key={g} className="absolute left-0 right-0 border-t border-dashed border-cyan-300/10" style={{ bottom: `${g * 100}%` }} />)}
      <div className="absolute inset-0 flex items-end gap-1.5">
        {data.map((x, i) => {
          const last = i === data.length - 1;
          return (
            <div key={i} className="flex-1 h-full flex flex-col justify-end items-center">
              <div className="w-full rounded-t-md nx-grow" title={`${x.d || x.w}: ${x.v}`}
                style={{ height: `${Math.max(3, (x.v / max) * 100)}%`, background: `linear-gradient(180deg, ${col}, ${col}33)`,
                  boxShadow: last ? `0 0 16px ${col}` : "none", opacity: last ? 1 : 0.75, animationDelay: `${i * 0.04}s` }} />
              {labels && <span className="nx-mono text-[11px] text-cyan-300/40 mt-1">{(x.d || x.w || "").slice(-2)}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Stat = ({ l, v }) => (
  <div className="rounded-xl border border-cyan-300/15 bg-cyan-300/[0.03] px-3 py-2">
    <p className="nx-mono text-xs tracking-widest text-cyan-300/60">{l}</p><p className="text-3xl font-black text-cyan-50">{v}</p>
  </div>
);

const Biz = ({ b, compact }) => {
  if (b.error) return <Card title={b.name.toUpperCase()} accent="#EF4444" code="LINK·DOWN" testid={`tv-biz-${b.id}`}><Empty>Can't reach this business right now.</Empty></Card>;
  const col = HC[b.health];
  return (
    <div className="nx-hud rounded-3xl p-6 flex flex-col gap-4 min-h-0" style={{ borderColor: `${col}55` }} data-testid={`tv-biz-${b.id}`}>
      <Corners accent={col} />
      <div className="flex items-center gap-5 relative z-10">
        <Ring value={b.score ?? 50} color={col} size={compact ? 96 : 120} sub={b.health.toUpperCase()} />
        <div className="min-w-0">
          <p className="nx-orbitron text-2xl font-black tracking-wider text-white truncate" style={{ textShadow: `0 0 14px ${col}66` }}>{b.name}</p>
          <p className="nx-mono text-sm tracking-widest" style={{ color: `${col}cc` }}>HEALTH INDEX · LIVE LINK</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 relative z-10">
        <Stat l="TODAY" v={fmt(b.sales)} /><Stat l="ORDERS" v={b.orders ?? "—"} />
        {b.cash != null ? <Stat l="CASH" v={fmt(b.cash)} /> : <Stat l="THIS WEEK" v={fmt(b.weekly.length ? b.weekly[b.weekly.length - 1].v : null)} />}
      </div>
      <div className="relative z-10"><p className="nx-mono text-sm tracking-widest text-cyan-300/60 mb-2">▸ DAILY REVENUE · 14D</p><Bars data={b.daily} col={col} h={compact ? 90 : 130} labels /></div>
      <div className="relative z-10"><p className="nx-mono text-sm tracking-widest text-cyan-300/60 mb-2">▸ WEEKLY REVENUE · 8W</p><Bars data={b.weekly} col="#38BDF8" h={compact ? 55 : 80} /></div>
      {b.alert && <p className="text-xl leading-snug text-amber-200 mt-auto relative z-10 border-l-4 border-amber-400/70 pl-3">{b.alert}</p>}
    </div>
  );
};

export const TvBusiness = ({ d }) => {
  const list = d.business || [];
  if (d.business === undefined) return <Card title="BUSINESS HEALTH" accent="#10B981" code="SYNC" testid="tv-scene-business"><Empty>Syncing your businesses…</Empty></Card>;
  if (!list.length) return (
    <Card title="BUSINESS HEALTH" accent="#10B981" code="NO·LINK" testid="tv-scene-business">
      <p className="text-3xl text-cyan-50 leading-snug">No businesses connected yet.</p>
      <p className="text-2xl text-cyan-100/50 mt-4">Open the TV Remote on your phone and press "Copy request for my business apps".</p>
    </Card>
  );
  return (
    <div className={`grid gap-5 h-full ${list.length === 1 ? "grid-cols-1" : list.length === 2 ? "grid-cols-2" : "grid-cols-3"}`} data-testid="tv-scene-business">
      {list.slice(0, 3).map((b) => <Biz key={b.id} b={b} compact={list.length > 2} />)}
    </div>
  );
};
